import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Card,
    CardContent,
    Grid,
    Modal,
    Alert,
    Snackbar,
    CircularProgress,
    Chip,
    IconButton,
    LinearProgress
} from '@mui/material';
import {
    FaTrophy,
    FaCoins,
    FaPlus,
    FaTrash,
    FaCheck,
    FaLock,
    FaDice,
    FaClock,
    FaPercent,
    FaBolt,
    FaArrowRotateRight
} from 'react-icons/fa6';
import { IoCloseCircleOutline, IoCheckmarkCircleOutline } from 'react-icons/io5';
import axios from 'axios';
import Nlayout from './Nlayout';
import { useAuth } from './authContext';
import coinImg from './Wild_coin.png';

const BASE_API_URL = 'https://backend.eldenguee.com';
const API_KEY = 'ed_qatar_2022_messi_dengueclarasoluna_2026';

export default function CrearPrediccion({ isMobile }) {
    const { username } = useAuth();
    const storedUser = localStorage.getItem('kick_user') || username || '';
    const cleanUser = storedUser.toLowerCase();
    const isAllowed = cleanUser === 'eldenguee' || cleanUser === 'lucaslunacl';

    // Tabs
    const [activeTab, setActiveTab] = useState('crear'); // 'crear' | 'abiertas' | 'cerradas' | 'resueltas'

    // Formulario de creación
    const [titulo, setTitulo] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [opciones, setOpciones] = useState(['Sí', 'No']);
    const [porcentajeCasa, setPorcentajeCasa] = useState('5');
    const [cierreMinutos, setCierreMinutos] = useState('');
    const [creating, setCreating] = useState(false);

    // Listado y gestión
    const [predicciones, setPredicciones] = useState([]);
    const [loadingList, setLoadingList] = useState(false);

    // Modal de resolución
    const [resolveModalOpen, setResolveModalOpen] = useState(false);
    const [predToResolve, setPredToResolve] = useState(null);
    const [selectedWinnerOption, setSelectedWinnerOption] = useState(null);
    const [resolving, setResolving] = useState(false);

    // Toast / Feedback
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    const showToast = (message, severity = 'success') => {
        setToast({ open: true, message, severity });
    };

    const getAuthHeaders = useCallback(() => {
        const token = localStorage.getItem('kick_jwt') || 'llluuuiiisss222000222666';
        return {
            'Content-Type': 'application/json',
            'X-Username': storedUser || 'eldenguee',
            'X-Session-Token': token,
            'x-api-key': API_KEY,
            'x-internal-api-key': API_KEY,
            Authorization: `Bearer ${token}`
        };
    }, [storedUser]);

    // Cargar predicciones del backend
    const fetchPredicciones = useCallback(async (tabKey = activeTab) => {
        if (tabKey === 'crear') return;
        try {
            setLoadingList(true);
            const estadoParam = tabKey === 'abiertas' ? 'abierta' : tabKey === 'cerradas' ? 'cerrada' : tabKey === 'canceladas' ? 'cancelada' : 'resuelta';
            const res = await axios.get(`${BASE_API_URL}/predicciones?estado=${estadoParam}&limit=40`);
            const data = res.data?.predicciones || [];
            setPredicciones(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error al cargar predicciones:', err);
            showToast('No se pudieron cargar las predicciones.', 'error');
            setPredicciones([]);
        } finally {
            setLoadingList(false);
        }
    }, [activeTab]);

    useEffect(() => {
        if (activeTab !== 'crear') {
            fetchPredicciones(activeTab);
        }
    }, [activeTab, fetchPredicciones]);

    // Opciones del formulario
    const handleAddOpcion = () => {
        if (opciones.length >= 6) {
            showToast('Máximo 6 opciones por predicción.', 'warning');
            return;
        }
        setOpciones([...opciones, `Opción ${opciones.length + 1}`]);
    };

    const handleRemoveOpcion = (idx) => {
        if (opciones.length <= 2) {
            showToast('Se requieren al menos 2 opciones.', 'warning');
            return;
        }
        setOpciones(opciones.filter((_, i) => i !== idx));
    };

    const handleUpdateOpcion = (val, idx) => {
        const copy = [...opciones];
        copy[idx] = val;
        setOpciones(copy);
    };

    // Crear predicción
    const handleCrear = async (e) => {
        e.preventDefault();
        if (!titulo.trim()) {
            showToast('Ingresá el título o pregunta de la predicción.', 'error');
            return;
        }
        const cleanOps = opciones.map(o => o.trim()).filter(Boolean);
        if (cleanOps.length < 2) {
            showToast('Debés incluir al menos 2 opciones con texto válido.', 'error');
            return;
        }

        try {
            setCreating(true);
            let cierreAt = null;
            if (cierreMinutos && !isNaN(cierreMinutos) && Number(cierreMinutos) > 0) {
                cierreAt = new Date(Date.now() + Number(cierreMinutos) * 60 * 1000).toISOString();
            }

            await axios.post(
                `${BASE_API_URL}/predicciones`,
                {
                    titulo: titulo.trim(),
                    descripcion: descripcion.trim(),
                    opciones: cleanOps,
                    porcentaje_casa: parseFloat(porcentajeCasa) || 5,
                    cierre_at: cierreAt
                },
                { headers: getAuthHeaders() }
            );

            showToast('¡Predicción creada con éxito! 🎯 Ya está disponible en la app.');
            setTitulo('');
            setDescripcion('');
            setOpciones(['Sí', 'No']);
            setPorcentajeCasa('5');
            setCierreMinutos('');
            setActiveTab('abiertas');
        } catch (err) {
            console.error('Error al crear predicción:', err);
            const msg = err.response?.data?.error || err.message || 'Error al crear la predicción.';
            showToast(msg, 'error');
        } finally {
            setCreating(false);
        }
    };

    // Cerrar apuestas de una predicción
    const handleCerrar = async (pred) => {
        if (!window.confirm(`¿Cerrar apuestas para "${pred.titulo}"? Ya nadie más podrá apostar.`)) {
            return;
        }
        try {
            await axios.post(
                `${BASE_API_URL}/predicciones/${pred.id}/cerrar`,
                {},
                { headers: getAuthHeaders() }
            );
            showToast('Apuestas cerradas correctamente.');
            fetchPredicciones(activeTab);
        } catch (err) {
            console.error('Error al cerrar apuestas:', err);
            showToast(err.response?.data?.error || 'No se pudo cerrar la predicción.', 'error');
        }
    };

    // Cancelar predicción y reembolsar D-Coins
    const handleCancelar = async (pred) => {
        if (!window.confirm(`¿Cancelar la predicción "${pred.titulo}"?\n\nSe devolverán todos los D-Coins a los apostadores participantes.`)) {
            return;
        }
        try {
            const res = await axios.post(
                `${BASE_API_URL}/predicciones/${pred.id}/cancelar`,
                {},
                { headers: getAuthHeaders() }
            );
            showToast(res.data?.message || '¡Predicción cancelada y D-Coins devueltos!');
            fetchPredicciones(activeTab);
        } catch (err) {
            console.error('Error al cancelar predicción:', err);
            showToast(err.response?.data?.error || 'No se pudo cancelar la predicción.', 'error');
        }
    };

    // Abrir modal de resolución
    const handleOpenResolve = (pred) => {
        setPredToResolve(pred);
        setSelectedWinnerOption(pred.opciones?.[0]?.id || null);
        setResolveModalOpen(true);
    };

    // Confirmar resolución
    const handleConfirmResolve = async () => {
        if (!predToResolve || !selectedWinnerOption) return;
        const winnerOpt = predToResolve.opciones?.find(o => o.id === selectedWinnerOption);
        const confirmMsg = `¿Declarar a "${winnerOpt?.nombre}" como la opción ganadora?\n\nLas ganancias se transferirán inmediatamente a los apostadores ganadores.`;
        if (!window.confirm(confirmMsg)) return;

        try {
            setResolving(true);
            const res = await axios.post(
                `${BASE_API_URL}/predicciones/${predToResolve.id}/resolver`,
                { opcion_ganadora_id: selectedWinnerOption },
                { headers: getAuthHeaders() }
            );
            showToast(res.data?.message || '¡Predicción resuelta con éxito! Ganancias pagadas.');
            setResolveModalOpen(false);
            setPredToResolve(null);
            fetchPredicciones(activeTab);
        } catch (err) {
            console.error('Error al resolver predicción:', err);
            showToast(err.response?.data?.error || 'No se pudo resolver la predicción.', 'error');
        } finally {
            setResolving(false);
        }
    };

    if (!isAllowed) {
        return (
            <Nlayout>
                <Box
                    sx={{
                        minHeight: '70vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 3,
                        textAlign: 'center'
                    }}
                >
                    <Card
                        sx={{
                            maxWidth: 480,
                            p: 4,
                            background: 'rgba(20, 24, 38, 0.85)',
                            backdropFilter: 'blur(16px)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: '24px'
                        }}
                    >
                        <Box sx={{ color: '#ef4444', fontSize: 48, mb: 2 }}>
                            <FaLock />
                        </Box>
                        <Typography sx={{ color: '#fff', fontSize: 24, fontWeight: 700, fontFamily: 'Outfit, sans-serif', mb: 1 }}>
                            Acceso Restringido
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, fontFamily: 'Outfit, sans-serif' }}>
                            Este panel es exclusivo para los usuarios <b>eldenguee</b> y <b>lucaslunacl</b>.
                        </Typography>
                    </Card>
                </Box>
            </Nlayout>
        );
    }

    return (
        <Nlayout>
            <Box
                sx={{
                    width: '100%',
                    minHeight: '100vh',
                    pt: isMobile ? '80px' : '110px',
                    pb: isMobile ? '120px' : '80px',
                    px: isMobile ? 2 : 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}
            >
                {/* Cabecera Principal */}
                <Box sx={{ maxWidth: 960, width: '100%', mb: 4, textAlign: isMobile ? 'center' : 'left' }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.6, borderRadius: '99px', background: 'rgba(0, 255, 115, 0.12)', border: '1px solid rgba(0, 255, 115, 0.3)', mb: 1.5 }}>
                        <FaBolt style={{ color: '#00ff73', fontSize: 13 }} />
                        <Typography sx={{ color: '#00ff73', fontSize: 12, fontWeight: 700, letterSpacing: '0.5px', fontFamily: 'Outfit, sans-serif', textTransform: 'uppercase' }}>
                            Panel de Administración
                        </Typography>
                    </Box>
                    <Typography
                        sx={{
                            color: '#ffffff',
                            fontWeight: 800,
                            fontSize: isMobile ? 28 : 38,
                            fontFamily: 'Outfit, sans-serif',
                            lineHeight: 1.2,
                            letterSpacing: '-0.02em'
                        }}
                    >
                        Predicciones con D-Coins
                    </Typography>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.65)',
                            fontSize: isMobile ? 13 : 15,
                            fontFamily: 'Outfit, sans-serif',
                            mt: 1
                        }}
                    >
                        Creá nuevas predicciones en vivo para el stream y administrá el cierre y pago de premios de los apostadores.
                    </Typography>

                    {/* Barra de Tabs */}
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            mt: 3,
                            p: 0.6,
                            borderRadius: '16px',
                            background: 'rgba(255, 255, 255, 0.04)',
                            border: '1px solid rgba(255, 255, 255, 0.08)',
                            width: 'fit-content',
                            flexWrap: 'wrap',
                            justifyContent: isMobile ? 'center' : 'flex-start'
                        }}
                    >
                        {[
                            { key: 'crear', label: 'Crear Predicción', icon: <FaPlus /> },
                            { key: 'abiertas', label: 'En Vivo (Abiertas)', icon: <FaDice /> },
                            { key: 'cerradas', label: 'Cerradas', icon: <FaLock /> },
                            { key: 'resueltas', label: 'Finalizadas', icon: <FaTrophy /> },
                            { key: 'canceladas', label: 'Canceladas', icon: <IoCloseCircleOutline /> },
                        ].map(t => {
                            const isActive = activeTab === t.key;
                            return (
                                <Button
                                    key={t.key}
                                    onClick={() => setActiveTab(t.key)}
                                    startIcon={t.icon}
                                    sx={{
                                        px: 2.2,
                                        py: 1,
                                        borderRadius: '12px',
                                        fontSize: 13,
                                        fontWeight: 700,
                                        fontFamily: 'Outfit, sans-serif',
                                        textTransform: 'none',
                                        transition: 'all 0.2s',
                                        color: isActive ? '#052e16' : 'rgba(255,255,255,0.7)',
                                        background: isActive
                                            ? 'linear-gradient(135deg, #00ff73 0%, #10b981 100%)'
                                            : 'transparent',
                                        boxShadow: isActive ? '0 4px 14px rgba(0,255,115,0.3)' : 'none',
                                        '&:hover': {
                                            background: isActive
                                                ? 'linear-gradient(135deg, #00ff73 0%, #10b981 100%)'
                                                : 'rgba(255,255,255,0.06)',
                                            color: isActive ? '#052e16' : '#ffffff'
                                        }
                                    }}
                                >
                                    {t.label}
                                </Button>
                            );
                        })}

                        {activeTab !== 'crear' && (
                            <IconButton
                                onClick={() => fetchPredicciones(activeTab)}
                                title="Actualizar lista"
                                sx={{
                                    color: '#00ff73',
                                    borderRadius: '12px',
                                    '&:hover': { background: 'rgba(0, 255, 115, 0.1)' }
                                }}
                            >
                                <FaArrowRotateRight size={14} />
                            </IconButton>
                        )}
                    </Box>
                </Box>

                {/* ─── TAB 1: FORMULARIO CREAR ─── */}
                {activeTab === 'crear' && (
                    <Card
                        sx={{
                            maxWidth: 780,
                            width: '100%',
                            background: 'rgba(15, 18, 30, 0.88)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid rgba(255, 255, 255, 0.08)',
                            borderRadius: '24px',
                            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 30px rgba(0, 255, 115, 0.05)',
                            overflow: 'hidden'
                        }}
                    >
                        <Box sx={{ p: isMobile ? 2.5 : 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                <Box sx={{ width: 42, height: 42, borderRadius: '12px', background: 'rgba(0, 255, 115, 0.12)', border: '1px solid rgba(0, 255, 115, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <FaDice style={{ color: '#00ff73', fontSize: 20 }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ color: '#fff', fontSize: 20, fontWeight: 700, fontFamily: 'Outfit, sans-serif' }}>
                                        Nueva Predicción
                                    </Typography>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, fontFamily: 'Outfit, sans-serif' }}>
                                        Definí la pregunta, las opciones y el porcentaje de comisión.
                                    </Typography>
                                </Box>
                            </Box>

                            <form onSubmit={handleCrear}>
                                {/* Título / Pregunta */}
                                <Box sx={{ mb: 2.5 }}>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 600, fontFamily: 'Outfit, sans-serif', mb: 0.8 }}>
                                        Pregunta / Título *
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        placeholder="Ej: ¿Gana la próxima partida de Warzone?"
                                        value={titulo}
                                        onChange={(e) => setTitulo(e.target.value)}
                                        variant="outlined"
                                        inputProps={{ maxLength: 200 }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                                                borderRadius: '12px',
                                                color: '#fff',
                                                fontFamily: 'Outfit, sans-serif',
                                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                                '&:hover fieldset': { borderColor: 'rgba(0, 255, 115, 0.4)' },
                                                '&.Mui-focused fieldset': { borderColor: '#00ff73' }
                                            }
                                        }}
                                    />
                                </Box>

                                {/* Descripción opcional */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 600, fontFamily: 'Outfit, sans-serif', mb: 0.8 }}>
                                        Descripción / Reglas (opcional)
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={2}
                                        placeholder="Detalles sobre mapa, condición de victoria, tiempo de espera..."
                                        value={descripcion}
                                        onChange={(e) => setDescripcion(e.target.value)}
                                        variant="outlined"
                                        inputProps={{ maxLength: 500 }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                                                borderRadius: '12px',
                                                color: '#fff',
                                                fontFamily: 'Outfit, sans-serif',
                                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                                '&:hover fieldset': { borderColor: 'rgba(0, 255, 115, 0.4)' },
                                                '&.Mui-focused fieldset': { borderColor: '#00ff73' }
                                            }
                                        }}
                                    />
                                </Box>

                                {/* Opciones de apuesta */}
                                <Box sx={{ mb: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
                                        <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 600, fontFamily: 'Outfit, sans-serif' }}>
                                            Opciones de apuesta (de 2 a 6 opciones)
                                        </Typography>
                                        {opciones.length < 6 && (
                                            <Button
                                                onClick={handleAddOpcion}
                                                startIcon={<FaPlus size={11} />}
                                                size="small"
                                                sx={{
                                                    color: '#a855f7',
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    fontFamily: 'Outfit, sans-serif',
                                                    textTransform: 'none',
                                                    background: 'rgba(168, 85, 247, 0.1)',
                                                    borderRadius: '8px',
                                                    px: 1.5,
                                                    '&:hover': { background: 'rgba(168, 85, 247, 0.2)' }
                                                }}
                                            >
                                                Agregar opción
                                            </Button>
                                        )}
                                    </Box>

                                    <Grid container spacing={1.5}>
                                        {opciones.map((op, idx) => (
                                            <Grid item xs={12} sm={6} key={idx}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        background: 'rgba(255, 255, 255, 0.02)',
                                                        p: 0.8,
                                                        borderRadius: '12px',
                                                        border: '1px solid rgba(255, 255, 255, 0.06)'
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 28,
                                                            height: 28,
                                                            borderRadius: '8px',
                                                            background: idx === 0 ? 'rgba(0, 255, 115, 0.2)' : idx === 1 ? 'rgba(239, 68, 68, 0.2)' : 'rgba(168, 85, 247, 0.2)',
                                                            color: idx === 0 ? '#00ff73' : idx === 1 ? '#ef4444' : '#a855f7',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: 12,
                                                            fontWeight: 800,
                                                            flexShrink: 0
                                                        }}
                                                    >
                                                        {idx + 1}
                                                    </Box>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        value={op}
                                                        onChange={(e) => handleUpdateOpcion(e.target.value, idx)}
                                                        placeholder={`Opción ${idx + 1}`}
                                                        inputProps={{ maxLength: 60 }}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                color: '#fff',
                                                                fontSize: 13,
                                                                fontFamily: 'Outfit, sans-serif',
                                                                '& fieldset': { border: 'none' }
                                                            }
                                                        }}
                                                    />
                                                    {opciones.length > 2 && (
                                                        <IconButton
                                                            onClick={() => handleRemoveOpcion(idx)}
                                                            size="small"
                                                            sx={{ color: '#ef4444', opacity: 0.8, '&:hover': { opacity: 1 } }}
                                                        >
                                                            <FaTrash size={12} />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>

                                {/* Parámetros adicionales */}
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 600, fontFamily: 'Outfit, sans-serif', mb: 0.8 }}>
                                            Comisión casa (% plataforma)
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            value={porcentajeCasa}
                                            onChange={(e) => setPorcentajeCasa(e.target.value)}
                                            placeholder="5"
                                            InputProps={{
                                                endAdornment: <FaPercent style={{ color: '#9ca3af', fontSize: 12 }} />
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: 'rgba(255, 255, 255, 0.03)',
                                                    borderRadius: '12px',
                                                    color: '#fff',
                                                    fontFamily: 'Outfit, sans-serif',
                                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                                    '&:hover fieldset': { borderColor: 'rgba(0, 255, 115, 0.4)' },
                                                    '&.Mui-focused fieldset': { borderColor: '#00ff73' }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 600, fontFamily: 'Outfit, sans-serif', mb: 0.8 }}>
                                            Cierre automático en (minutos, opcional)
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            value={cierreMinutos}
                                            onChange={(e) => setCierreMinutos(e.target.value)}
                                            placeholder="Ej: 15 (dejar vacío para cierre manual)"
                                            InputProps={{
                                                endAdornment: <FaClock style={{ color: '#9ca3af', fontSize: 13 }} />
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: 'rgba(255, 255, 255, 0.03)',
                                                    borderRadius: '12px',
                                                    color: '#fff',
                                                    fontFamily: 'Outfit, sans-serif',
                                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                                    '&:hover fieldset': { borderColor: 'rgba(0, 255, 115, 0.4)' },
                                                    '&.Mui-focused fieldset': { borderColor: '#00ff73' }
                                                }
                                            }}
                                        />
                                    </Grid>
                                </Grid>

                                {/* Botón Publicar */}
                                <Button
                                    type="submit"
                                    fullWidth
                                    disabled={creating}
                                    sx={{
                                        py: 1.8,
                                        borderRadius: '14px',
                                        fontSize: 15,
                                        fontWeight: 800,
                                        fontFamily: 'Outfit, sans-serif',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        color: '#052e16',
                                        background: 'linear-gradient(135deg, #00ff73 0%, #10b981 100%)',
                                        boxShadow: '0 8px 25px rgba(0,255,115,0.35)',
                                        transition: 'all 0.25s',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #22c55e 0%, #059669 100%)',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 12px 30px rgba(0,255,115,0.5)'
                                        },
                                        '&.Mui-disabled': {
                                            background: 'rgba(0,255,115,0.3)',
                                            color: '#052e16'
                                        }
                                    }}
                                >
                                    {creating ? <CircularProgress size={22} sx={{ color: '#052e16' }} /> : 'Publicar Predicción en Vivo 🎯'}
                                </Button>
                            </form>
                        </Box>
                    </Card>
                )}

                {/* ─── TABS 2, 3, 4: LISTADO Y ACCIONES ADMIN ─── */}
                {activeTab !== 'crear' && (
                    <Box sx={{ maxWidth: 960, width: '100%' }}>
                        {loadingList ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                                <CircularProgress sx={{ color: '#00ff73' }} />
                            </Box>
                        ) : predicciones.length === 0 ? (
                            <Card
                                sx={{
                                    p: 6,
                                    textAlign: 'center',
                                    background: 'rgba(15, 18, 30, 0.65)',
                                    borderRadius: '20px',
                                    border: '1px solid rgba(255, 255, 255, 0.06)'
                                }}
                            >
                                <FaDice style={{ fontSize: 44, color: 'rgba(255,255,255,0.3)', marginBottom: 12 }} />
                                <Typography sx={{ color: '#fff', fontSize: 18, fontWeight: 700, fontFamily: 'Outfit, sans-serif' }}>
                                    No hay predicciones en esta sección
                                </Typography>
                                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, fontFamily: 'Outfit, sans-serif', mt: 0.5 }}>
                                    Hacé clic en &quot;Crear Predicción&quot; para abrir una nueva apuesta en vivo.
                                </Typography>
                            </Card>
                        ) : (
                            <Grid container spacing={2.5}>
                                {predicciones.map(pred => {
                                    const isAbierta = pred.estado === 'abierta';
                                    const isResuelta = pred.estado === 'resuelta';
                                    const isCancelada = pred.estado === 'cancelada';
                                    const isCerrada = pred.estado === 'cerrada';
                                    const pozoTotal = pred.pozo_total || 0;

                                    return (
                                        <Grid item xs={12} md={6} key={pred.id}>
                                            <Card
                                                sx={{
                                                    background: 'rgba(15, 18, 30, 0.85)',
                                                    backdropFilter: 'blur(16px)',
                                                    borderRadius: '20px',
                                                    border: isAbierta
                                                        ? '1px solid rgba(0, 255, 115, 0.25)'
                                                        : isResuelta
                                                            ? '1px solid rgba(168, 85, 247, 0.25)'
                                                            : isCancelada
                                                                ? '1px solid rgba(239, 68, 68, 0.25)'
                                                                : '1px solid rgba(245, 158, 11, 0.25)',
                                                    p: 2.5,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 2,
                                                    boxShadow: '0 8px 30px rgba(0,0,0,0.35)',
                                                    transition: 'all 0.25s',
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: '0 12px 35px rgba(0,0,0,0.5)'
                                                    }
                                                }}
                                            >
                                                {/* Header de la tarjeta */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Chip
                                                        label={isAbierta ? 'EN VIVO' : isResuelta ? 'FINALIZADA' : isCancelada ? 'CANCELADA' : 'CERRADA'}
                                                        size="small"
                                                        sx={{
                                                            fontWeight: 800,
                                                            fontSize: 10.5,
                                                            fontFamily: 'Outfit, sans-serif',
                                                            backgroundColor: isAbierta ? 'rgba(0,255,115,0.15)' : isResuelta ? 'rgba(168,85,247,0.15)' : isCancelada ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)',
                                                            color: isAbierta ? '#00ff73' : isResuelta ? '#a855f7' : isCancelada ? '#ef4444' : '#f59e0b',
                                                            border: `1px solid ${isAbierta ? '#00ff7333' : isResuelta ? '#a855f733' : isCancelada ? '#ef444433' : '#f59e0b33'}`
                                                        }}
                                                    />
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, background: 'rgba(251, 191, 36, 0.1)', px: 1.2, py: 0.4, borderRadius: '99px', border: '1px solid rgba(251, 191, 36, 0.25)' }}>
                                                        <img src={coinImg} alt="Coins" style={{ width: 14, height: 14 }} />
                                                        <Typography sx={{ color: '#fbbf24', fontSize: 12, fontWeight: 700, fontFamily: 'Outfit, sans-serif' }}>
                                                            {Number(pozoTotal).toLocaleString()} coins
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                {/* Título & Descripción */}
                                                <Box>
                                                    <Typography sx={{ color: '#ffffff', fontSize: 16, fontWeight: 700, fontFamily: 'Outfit, sans-serif', lineHeight: 1.3 }}>
                                                        {pred.titulo}
                                                    </Typography>
                                                    {pred.descripcion ? (
                                                        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, fontFamily: 'Outfit, sans-serif', mt: 0.5 }}>
                                                            {pred.descripcion}
                                                        </Typography>
                                                    ) : null}
                                                </Box>

                                                {/* Opciones con barras de progreso */}
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    {(pred.opciones || []).map((op) => {
                                                        const isWinner = isResuelta && (pred.opcion_ganadora === op.id || pred.opcion_ganadora_id === op.id);
                                                        const pct = op.porcentaje || 0;
                                                        return (
                                                            <Box
                                                                key={op.id}
                                                                sx={{
                                                                    position: 'relative',
                                                                    p: 1.2,
                                                                    borderRadius: '10px',
                                                                    background: isWinner ? 'rgba(168, 85, 247, 0.15)' : 'rgba(255, 255, 255, 0.03)',
                                                                    border: `1px solid ${isWinner ? 'rgba(168, 85, 247, 0.5)' : 'rgba(255, 255, 255, 0.06)'}`,
                                                                    overflow: 'hidden'
                                                                }}
                                                            >
                                                                {/* Barra de progreso */}
                                                                <Box
                                                                    sx={{
                                                                        position: 'absolute',
                                                                        left: 0,
                                                                        top: 0,
                                                                        bottom: 0,
                                                                        width: `${Math.max(2, pct)}%`,
                                                                        backgroundColor: isWinner ? 'rgba(168, 85, 247, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                                                                        transition: 'width 0.3s ease'
                                                                    }}
                                                                />
                                                                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                    <Typography sx={{ color: isWinner ? '#e9d5ff' : '#ffffff', fontSize: 13, fontWeight: isWinner ? 800 : 600, fontFamily: 'Outfit, sans-serif' }}>
                                                                        {isWinner ? '🏆 ' : ''}{op.nombre}
                                                                    </Typography>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                        <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, fontFamily: 'Outfit, sans-serif' }}>
                                                                            {Number(op.total_apostado || 0).toLocaleString()} coins
                                                                        </Typography>
                                                                        <Typography sx={{ color: isWinner ? '#c084fc' : '#00ff73', fontSize: 12, fontWeight: 700, fontFamily: 'Outfit, sans-serif' }}>
                                                                            {pct}%
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>

                                                {/* Footer de datos */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                                                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, fontFamily: 'Outfit, sans-serif' }}>
                                                        Comisión: {pred.porcentaje_casa || 5}% · Creada por {pred.created_by || 'admin'}
                                                    </Typography>
                                                    {pred.total_apostadores != null && (
                                                        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontFamily: 'Outfit, sans-serif' }}>
                                                            {pred.total_apostadores} {pred.total_apostadores === 1 ? 'apostador' : 'apostadores'}
                                                        </Typography>
                                                    )}
                                                </Box>

                                                {/* Botones de acción admin */}
                                                {!isResuelta && !isCancelada && (
                                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                        {isAbierta && (
                                                            <Button
                                                                fullWidth
                                                                onClick={() => handleCerrar(pred)}
                                                                startIcon={<FaLock size={12} />}
                                                                sx={{
                                                                    py: 1,
                                                                    borderRadius: '10px',
                                                                    fontSize: 12,
                                                                    fontWeight: 700,
                                                                    fontFamily: 'Outfit, sans-serif',
                                                                    textTransform: 'none',
                                                                    color: '#f59e0b',
                                                                    background: 'rgba(245, 158, 11, 0.12)',
                                                                    border: '1px solid rgba(245, 158, 11, 0.3)',
                                                                    '&:hover': {
                                                                        background: 'rgba(245, 158, 11, 0.22)'
                                                                    }
                                                                }}
                                                            >
                                                                Cerrar
                                                            </Button>
                                                        )}
                                                        <Button
                                                            fullWidth
                                                            onClick={() => handleOpenResolve(pred)}
                                                            startIcon={<FaTrophy size={12} />}
                                                            sx={{
                                                                py: 1,
                                                                borderRadius: '10px',
                                                                fontSize: 12,
                                                                fontWeight: 700,
                                                                fontFamily: 'Outfit, sans-serif',
                                                                textTransform: 'none',
                                                                color: '#052e16',
                                                                background: 'linear-gradient(135deg, #00ff73 0%, #10b981 100%)',
                                                                boxShadow: '0 4px 14px rgba(0,255,115,0.25)',
                                                                '&:hover': {
                                                                    background: 'linear-gradient(135deg, #22c55e 0%, #059669 100%)'
                                                                }
                                                            }}
                                                        >
                                                            Ganador
                                                        </Button>
                                                        <Button
                                                            fullWidth
                                                            onClick={() => handleCancelar(pred)}
                                                            startIcon={<IoCloseCircleOutline size={14} />}
                                                            sx={{
                                                                py: 1,
                                                                borderRadius: '10px',
                                                                fontSize: 12,
                                                                fontWeight: 700,
                                                                fontFamily: 'Outfit, sans-serif',
                                                                textTransform: 'none',
                                                                color: '#ef4444',
                                                                background: 'rgba(239, 68, 68, 0.12)',
                                                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                                                '&:hover': {
                                                                    background: 'rgba(239, 68, 68, 0.22)'
                                                                }
                                                            }}
                                                        >
                                                            Cancelar
                                                        </Button>
                                                    </Box>
                                                )}
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        )}
                    </Box>
                )}
            </Box>

            {/* ─── MODAL DECLARAR GANADOR ─── */}
            <Modal
                open={resolveModalOpen}
                onClose={() => !resolving && setResolveModalOpen(false)}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: isMobile ? '92vw' : 480,
                        backgroundColor: '#0f121e',
                        borderRadius: '20px',
                        border: '1px solid rgba(255,255,255,0.1)',
                        boxShadow: '0 25px 60px rgba(0,0,0,0.85), 0 0 30px rgba(0, 255, 115, 0.1)',
                        p: 3.5,
                        outline: 'none'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FaTrophy style={{ color: '#fbbf24', fontSize: 20 }} />
                            <Typography sx={{ color: '#fff', fontSize: 18, fontWeight: 700, fontFamily: 'Outfit, sans-serif' }}>
                                Declarar Ganador
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={() => !resolving && setResolveModalOpen(false)}
                            sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fff' } }}
                        >
                            <IoCloseCircleOutline size={22} />
                        </IconButton>
                    </Box>

                    <Typography sx={{ color: '#ffffff', fontSize: 14, fontWeight: 600, fontFamily: 'Outfit, sans-serif', mb: 1 }}>
                        {predToResolve?.titulo}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, fontFamily: 'Outfit, sans-serif', mb: 2.5 }}>
                        Seleccioná la opción ganadora. Al confirmar, el pozo neto será transferido automáticamente a las cuentas de los acertantes.
                    </Typography>

                    {/* Selector de opciones */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, mb: 3 }}>
                        {(predToResolve?.opciones || []).map(op => {
                            const isSelected = selectedWinnerOption === op.id;
                            return (
                                <Box
                                    key={op.id}
                                    onClick={() => setSelectedWinnerOption(op.id)}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: '12px',
                                        cursor: 'pointer',
                                        border: isSelected ? '2px solid #00ff73' : '1px solid rgba(255,255,255,0.08)',
                                        background: isSelected ? 'rgba(0, 255, 115, 0.1)' : 'rgba(255, 255, 255, 0.03)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {isSelected && <FaCheck style={{ color: '#00ff73', fontSize: 14 }} />}
                                        <Typography sx={{ color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: 'Outfit, sans-serif' }}>
                                            {op.nombre}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ color: '#fbbf24', fontSize: 12, fontWeight: 600, fontFamily: 'Outfit, sans-serif' }}>
                                        {Number(op.total_apostado || 0).toLocaleString()} coins
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Pozo total & botón */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, p: 1.5, borderRadius: '10px', background: 'rgba(255,255,255,0.03)' }}>
                        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, fontFamily: 'Outfit, sans-serif' }}>
                            Pozo Total a Distribuir:
                        </Typography>
                        <Typography sx={{ color: '#00ff73', fontSize: 14, fontWeight: 800, fontFamily: 'Outfit, sans-serif' }}>
                            {Number(predToResolve?.pozo_total || 0).toLocaleString()} D-Coins
                        </Typography>
                    </Box>

                    <Button
                        fullWidth
                        disabled={resolving || !selectedWinnerOption}
                        onClick={handleConfirmResolve}
                        sx={{
                            py: 1.5,
                            borderRadius: '12px',
                            fontSize: 14,
                            fontWeight: 800,
                            fontFamily: 'Outfit, sans-serif',
                            color: '#052e16',
                            background: 'linear-gradient(135deg, #00ff73 0%, #10b981 100%)',
                            boxShadow: '0 6px 20px rgba(0,255,115,0.3)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #22c55e 0%, #059669 100%)'
                            }
                        }}
                    >
                        {resolving ? <CircularProgress size={20} sx={{ color: '#052e16' }} /> : 'Confirmar y Pagar Premios 🏆'}
                    </Button>
                </Box>
            </Modal>

            {/* Toast feedback */}
            <Snackbar
                open={toast.open}
                autoHideDuration={4000}
                onClose={() => setToast(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setToast(prev => ({ ...prev, open: false }))}
                    severity={toast.severity}
                    variant="filled"
                    sx={{ width: '100%', fontFamily: 'Outfit, sans-serif', fontWeight: 600 }}
                >
                    {toast.message}
                </Alert>
            </Snackbar>
        </Nlayout>
    );
}
